import React, { useState, useEffect } from 'react';
import { Row } from 'reactstrap';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { InView } from 'react-intersection-observer';

import Meta from 'Components/Utility/SEO/Meta';
import InfiniteMore from 'Components/Utility/InfiniteMore';
import HitCard from 'Components/Cards/HitCard';
import SkeletonHitCard from 'Components/Cards/SkeletonHitCard';
import TopSellersCard from 'Components/Cards/TopSellersCard/TopSellersCard';
import TopSellersSkeletonCard from 'Components/Cards/TopSellersCard/TopSellersSkeletonCard';
import Backtotopbutton from 'Components/Utility/Mobile/Backtotopbutton';
import { fetchTopSellers } from 'Redux/ducks/algolia-calls';
import PillFilters from 'Components/Utility/PillFilters';
import 'Features/StaticCategory/pages/TopSellersPage.scss';


const messages = defineMessages({
  title: {
    id: 'topsellers.title',
    defaultMessage: 'Top Sellers | Most popular PC and Steam Games | Fanatical',
  },
  meta_description: {
    id: 'topsellers.meta_description',
    defaultMessage: 'Find the most popular deals on PC Games and Steam Games',
  },
  alltopsellers: {
    id: 'topsellers.alltopsellers',
    defaultMessage: 'All Top Sellers',
  },
  books: {
    id: 'topsellers.books',
    defaultMessage: 'Books',
  },
  comics: {
    id: 'topsellers.comics',
    defaultMessage: 'Comics',
  },
  software: {
    id: 'topsellers.software',
    defaultMessage: 'Software',
  },
  games: {
    id: 'topsellers.games',
    defaultMessage: 'Games',
  },
  audio: {
    id: 'topsellers.audio',
    defaultMessage: 'Audio',
  },
  video: {
    id: 'topsellers.video',
    defaultMessage: 'Video',
  },
});

function TopSellersPage() {
  const { formatMessage } = useIntl();
  const [availableTypes, setAvailableTypes] = useState([]);
  const [hits, setHits] = useState(null);
  const [type, setType] = useState('all');
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    const fetchHits = async () => {
      setHits([]);
      const result = await fetchTopSellers(type, 50);

      if (type === 'all') {
        setAvailableTypes(Object.keys(get(result, 'facets.display_type')));
      }

      setHits(result.hits);
    };

    fetchHits();
  }, [type]);


  const isGame = availableTypes.includes('game') || availableTypes.includes('bundle') || availableTypes.includes('dlc');
  const isEbook = availableTypes.includes('book-bundle') || availableTypes.includes('elearning-bundle') || availableTypes.includes('elearning') || availableTypes.includes('comic-bundle') || availableTypes.includes('comic');
  const isSoftware = availableTypes.includes('software-bundle') || availableTypes.includes('audio-bundle');

  const filters = [
    { name: formatMessage(messages.alltopsellers), type: 'all', live: true },
    { name: formatMessage(messages.games), type: 'games', live: isGame },
    { name: formatMessage(messages.books), type: 'books', live: isEbook },
    { name: formatMessage(messages.software), type: 'software', live: isSoftware },
  ].filter(t => t.live);


  const onFilterChange = (value) => {
    setType(value);
  };

  return (
    <main className="container">
      <Meta
        title={formatMessage(messages.title)}
        description={formatMessage(messages.meta_description)}
        url="top-sellers"
        hits={hits}
        pageType="CollectionPage"
      />
      <div className="pt-5 top-sellers">
        <InView>
          {({ inView, ref }) =>
            <div ref={ref} className="top-sellers-top-container">
              <div className="title-container">
                <div>
                  <h1>
                    <FormattedMessage id="top-sellers.header" defaultMessage="Top Sellers" />
                  </h1>
                  {!inView &&
                    <div className="d-block d-sm-none">
                      <Backtotopbutton />
                    </div>
                  }
                </div>
              </div>
            </div>
          }
        </InView>

        <p className="seo-blurb-text mb-4">
          <FormattedMessage
            id="top-sellers.intro"
            defaultMessage="Are you searching for the hottest sales on the site? Look no further than Top Sellers! You can find all the hottest offers on games, bundles, eBooks and software. Don’t miss out, bag yourself a bargain with our range of deals."
          />
        </p>

        <div className="categories-and-sortby-section d-flex">
          <PillFilters value={type} options={filters} onChange={onFilterChange} />
        </div>

        {isEmpty(hits) &&
          <div className="d-lg-none">
            <Row>
              <SkeletonHitCard
                numberOfCards={25}
                lgSize={3}
                mdSize={4}
                smSize={6}
                xsSize={6}
              />
            </Row>
          </div>
        }
        {isEmpty(hits) && [...Array(25).keys()].map(i =>
          <TopSellersSkeletonCard number={i + 1} key={i + 1} />,
        )}

        {!isEmpty(hits) &&
          <InfiniteMore
            loadMore={() => setShowMore(true)}
            hasMore={!showMore && hits.length > 25}
            initialLoad={false}
          >
            <div className="d-lg-none">
              <Row>
                {hits.slice(0, showMore ? 50 : 25).map(hit =>
                  <HitCard
                    key={`${hit.objectID} ${Math.random()}`}
                    hit={hit}
                    xsSize={6}
                    smSize={6}
                    mdSize={4}
                  />,
                )}
              </Row>
            </div>
            <ol className="d-none d-lg-block reset-list">
              {hits.slice(0, showMore ? 50 : 25).map((hit, index) =>
                <li>
                  <TopSellersCard
                    key={`${hit.objectID} ${Math.random()}`}
                    hit={hit}
                    index={index}
                  />
                </li>,
              )}
            </ol>
          </InfiniteMore>
        }
      </div>
    </main>
  );
}

export default TopSellersPage;
